import { Palette } from '@mui/material/styles';

import typography from './typography.js';

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    transparent: true;
    tertiary: true;
    yellow: true;
    teal: true;
    purple: true;
    silver: true;
    fuchsia: true;
    royal: true;
    mint: true;
    azure: true;
    blaze: true;
  }
}

const chipOverrides = (palette: Palette) => {
  return {
    variants: [
      {
        props: { color: 'silver' as const, variant: 'filled' as const },
        style: {
          '&:hover': {
            backgroundColor: palette.silver.main,
          },
          '& .MuiChip-label': {
            color: palette.silver.contrastText,
          },
          '& .MuiChip-icon': {
            color: palette.silver.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.silver.contrastText,
            opacity: '0.7',
            '&:hover': {
              color: palette.silver.contrastText,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'transparent' as const, variant: 'filled' as const },
        style: {
          '&:hover': {
            backgroundColor: palette.transparent.main,
          },
          '& .MuiChip-label': {
            color: palette.text.primary,
          },
          '& .MuiChip-icon': {
            color: palette.text.primary,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.text.primary,
            opacity: '0.7',
            '&:hover': {
              color: palette.text.primary,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'teal' as const, variant: 'filled' as const },

        style: {
          backgroundColor: palette.background.teal,
          '&:hover': {
            backgroundColor: palette.background.teal,
          },
        },
      },
      {
        props: { color: 'purple' as const, variant: 'filled' as const },

        style: {
          backgroundColor: palette.background.purple,
          '&:hover': {
            backgroundColor: palette.background.purple,
          },
        },
      },
      {
        props: { color: 'yellow' as const, variant: 'filled' as const },

        style: {
          backgroundColor: palette.background.yellow,
          '&:hover': {
            backgroundColor: palette.background.yellow,
          },
        },
      },
      {
        props: { color: 'error' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.error.light,
          '&:hover': {
            backgroundColor: palette.error.light,
          },
        },
      },
      {
        props: { color: 'fuchsia' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.background.fuchsia,
          '&:hover': {
            backgroundColor: palette.background.fuchsia,
          },
        },
      },
      {
        props: { color: 'royal' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.background.royal,
          '&:hover': {
            backgroundColor: palette.background.royal,
          },
        },
      },

      {
        props: { color: 'mint' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.background.mint,
          '&:hover': {
            backgroundColor: palette.background.mint,
          },
        },
      },
      {
        props: { color: 'azure' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.background.azure,
          '&:hover': {
            backgroundColor: palette.background.azure,
          },
        },
      },
      {
        props: { color: 'blaze' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.background.blaze,
          '&:hover': {
            backgroundColor: palette.background.blaze,
          },
        },
      },
      {
        props: { color: 'primary' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.primary.light,
          '&:hover': {
            backgroundColor: palette.primary.main,
          },
        },
      },
      {
        props: { color: 'secondary' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.secondary.main,
          '&:hover': {
            backgroundColor: palette.secondary.dark,
          },
        },
      },
      {
        props: { color: 'tertiary' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.tertiary.light,
          '&:hover': {
            backgroundColor: palette.ui.highlight,
          },

          '& .MuiChip-label': {
            color: palette.tertiary.dark,
          },
          '& .MuiChip-icon': {
            color: palette.tertiary.dark,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.tertiary.dark,
            opacity: '0.7',
            '&:hover': {
              color: palette.tertiary.dark,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'success' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.success.light,
          '&:hover': {
            backgroundColor: palette.success.light,
          },
        },
      },
      {
        props: { color: 'warning' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.warning.light,
          '&:hover': {
            backgroundColor: palette.warning.light,
          },
        },
      },
      {
        props: { color: 'info' as const, variant: 'filled' as const },
        style: {
          backgroundColor: palette.info.light,
          '&:hover': {
            backgroundColor: palette.info.light,
          },
        },
      },

      {
        props: { color: 'yellow' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.yellow.main}`,
          '& .MuiChip-label': {
            color: palette.yellow.contrastText,
          },
          '& .MuiChip-icon': {
            color: palette.yellow.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.yellow.contrastText,
            opacity: '0.7',
            '&:hover': {
              color: palette.yellow.contrastText,
              opacity: '1',
            },
          },
        },
      },

      {
        props: { color: 'teal' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.teal.main}`,
          '& .MuiChip-label': {
            color: palette.teal.contrastText,
          },
          '& .MuiChip-icon': {
            color: palette.teal.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.teal.contrastText,
            opacity: '0.7',
            '&:hover': {
              color: palette.teal.contrastText,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'purple' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.purple.main}`,
          '& .MuiChip-label': {
            color: palette.purple.contrastText,
          },
          '& .MuiChip-icon': {
            color: palette.purple.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.purple.contrastText,
            opacity: '0.7',
            '&:hover': {
              color: palette.purple.contrastText,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'fuchsia' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.fuchsia.main}`,
          '& .MuiChip-label': {
            color: palette.fuchsia.contrastText,
          },
          '& .MuiChip-icon': {
            color: palette.fuchsia.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.fuchsia.contrastText,
            opacity: '0.7',
            '&:hover': {
              color: palette.fuchsia.contrastText,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'royal' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.royal.main}`,
          '& .MuiChip-label': {
            color: palette.royal.contrastText,
          },
          '& .MuiChip-icon': {
            color: palette.royal.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.royal.contrastText,
            opacity: '0.7',
            '&:hover': {
              color: palette.royal.contrastText,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'mint' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.mint.main}`,
          '& .MuiChip-label': {
            color: palette.mint.contrastText,
          },
          '& .MuiChip-icon': {
            color: palette.mint.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.mint.contrastText,
            opacity: '0.7',
            '&:hover': {
              color: palette.mint.contrastText,
              opacity: '1',
            },
          },
        },
      },

      {
        props: { color: 'azure' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.azure.main}`,
          '& .MuiChip-label': {
            color: palette.azure.contrastText,
          },
          '& .MuiChip-icon': {
            color: palette.azure.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.azure.contrastText,
            opacity: '0.7',
            '&:hover': {
              color: palette.azure.contrastText,
              opacity: '1',
            },
          },
        },
      },

      {
        props: { color: 'blaze' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.blaze.main}`,
          '& .MuiChip-label': {
            color: palette.blaze.contrastText,
          },
          '& .MuiChip-icon': {
            color: palette.blaze.contrastText,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.blaze.contrastText,
            opacity: '0.7',
            '&:hover': {
              color: palette.blaze.contrastText,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'transparent' as const, variant: 'outlined' as const },
        style: {
          border: 'none !important',
          '& .MuiChip-label': {
            color: palette.text.primary,
          },
          '& .MuiChip-icon': {
            color: palette.text.primary,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.text.primary,
            opacity: '0.7',
            '&:hover': {
              color: palette.text.primary,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'primary' as const, variant: 'outlined' as const },
        style: {
          '&:hover': {
            backgroundColor: `${palette.background.primary} !important`,
          },
          border: `1px solid ${palette.primary.light}`,
          '& .MuiChip-label': {
            color: palette.primary.dark,
          },
          '& .MuiChip-icon': {
            color: palette.primary.dark,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.primary.dark,
            opacity: '0.7',
            '&:hover': {
              color: palette.primary.dark,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'secondary' as const, variant: 'outlined' as const },
        style: {
          '&:hover': {
            backgroundColor: `${palette.ui.hover} !important`,
          },
          border: `1px solid ${palette.secondary.light}`,
        },
      },
      {
        props: { color: 'tertiary' as const, variant: 'outlined' as const },
        style: {
          '&:hover': {
            backgroundColor: `${palette.background.tertiary} !important`,
          },
          border: `1px solid ${palette.tertiary.light}`,
          '& .MuiChip-label': {
            color: palette.tertiary.main,
          },
          '& .MuiChip-icon': {
            color: palette.tertiary.main,
          },
          '& .MuiChip-deleteIcon': {
            color: palette.tertiary.main,
            opacity: '0.7',
            '&:hover': {
              color: palette.tertiary.main,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'error' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.error.light}`,
          color: palette.error.dark,
          '& .MuiChip-deleteIcon': {
            color: palette.error.dark,
            opacity: '0.7',
            '&:hover': {
              color: palette.error.dark,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'success' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.success.light}`,
          color: palette.success.dark,
          '& .MuiChip-deleteIcon': {
            color: palette.success.dark,
            opacity: '0.7',
            '&:hover': {
              color: palette.success.dark,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'warning' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.warning.light}`,
          color: palette.warning.dark,
          '& .MuiChip-deleteIcon': {
            color: palette.warning.dark,
            opacity: '0.7',
            '&:hover': {
              color: palette.warning.dark,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'info' as const, variant: 'outlined' as const },
        style: {
          border: `1px solid ${palette.info.light}`,
          color: palette.info.dark,
          '& .MuiChip-deleteIcon': {
            color: palette.info.dark,
            opacity: '0.7',
            '&:hover': {
              color: palette.info.dark,
              opacity: '1',
            },
          },
        },
      },
      {
        props: { color: 'silver' as const, variant: 'outlined' as const },
        style: {
          color: palette.text.primary,
          border: `1px solid ${palette.silver.main}`,
          '& .MuiChip-deleteIcon': {
            color: palette.text.primary,
            opacity: '0.7',
            '&:hover': {
              color: palette.text.primary,
              opacity: '1',
            },
          },
        },
      },
    ],

    styleOverrides: {
      root: {
        ...typography.caption01,
        transition: 'none',
        paddingLeft: 8,
        paddingRight: 8,
        borderRadius: 12,
        '&:active': {
          boxShadow: 'none',
        },
      },
      outlined: {
        backgroundColor: `${palette.transparent.main} !important`,
        '&:hover': {
          backgroundColor: `${palette.transparent.main} !important`,
        },
      },
      label: {
        paddingRight: 0,
        paddingLeft: 0,
      },
      icon: {
        marginLeft: 0,
        marginRight: 0,
      },
      avatar: {
        margin: '0px',
      },
      deleteIcon: {
        margin: '0px 0px 0px -2px',
      },
      sizeSmall: {
        height: '1.25rem',
        padding: '2px 4px',
        gap: '2px',
      },
      sizeMedium: {
        height: '1.5rem',
        padding: '4px 8px',
        gap: '4px',
      },
    },
  };
};

export default chipOverrides;
